import React from 'react';
import { Card, CardContent } from "@/components/ui/card";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

const statCardVariants = cva(
  "glass-card overflow-hidden",
  {
    variants: {
      variant: {
        default: "border border-border",
        outline: "border border-border bg-background",
        purple: "border-roundone-light-purple/50 border",
        gradient: "border-0 bg-gradient-to-br from-[#5000FF] to-[#AB84FF] text-white",
        dark: "border border-[#35394F] bg-[#161826]", // Added dark variant
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

interface StatCardProps extends VariantProps<typeof statCardVariants> {
  title: string;
  value: string | number | React.ReactNode;
  icon: React.ReactNode;
  description?: string;
  trend?: {
    value: number;
    positive: boolean;
  };
  className?: string;
  onClick?: () => void;
  isString?: boolean;
}

const StatCard: React.FC<StatCardProps> = ({ 
  title, 
  value, 
  icon, 
  description,
  trend, 
  variant,
  className,
  onClick
}) => {
  const isGradient = variant === 'gradient';
  
  return (
    <Card 
      className={cn(
        statCardVariants({ variant }), 
        "transition-all duration-300 hover:shadow-lg", 
        onClick ? "cursor-pointer hover:scale-105" : "", 
        className
      )}
      onClick={onClick}
      style={isGradient ? {
        background: 'linear-gradient(to bottom right, #5000FF, #AB84FF)',
        color: 'white',
        border: 0
      } : undefined}
    >
      <CardContent className="p-6">
        <div className="flex justify-between items-start">
          <div className="space-y-2">
            <p className={`text-sm font-medium ${isGradient ? 'text-white/80' : 'text-muted-foreground'}`}>
              {title}
            </p>
            <div className="flex items-end gap-2">
              <h3 className={`text-2xl font-bold ${isGradient ? 'text-white' : ''}`}>{value}</h3>
              {trend && (
                <span className={`text-xs font-medium flex items-center ${trend.positive ? 'text-green-500' : 'text-red-500'}`}>
                  {trend.positive ? '↑' : '↓'} {Math.abs(trend.value)}%
                </span>
              )}
            </div>
            {description && (
              <p className={`text-xs ${isGradient ? 'text-white/80' : 'text-muted-foreground'} mt-1`}>
                {description}
              </p>
            )}
          </div>
          <div className={`p-3 rounded-full ${isGradient ? 'bg-white/20' : 'bg-roundone-light-purple/10'}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default StatCard;
